import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ProgressZone } from 'src/app/_shared/components/utils/circular-progress/circular-progress.component';
import { FleetGeneralFigureService } from 'src/app/_services/fleet-general-figure.service';
import { VehicleGeneralFigureService } from 'src/app/_services/vehicle-general-figure.service';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { NavigationService } from 'src/app/_services/navigation.service';
import { forkJoin } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { KmToMilesPipe } from 'src/app/_shared/pipes/km-to-miles.pipe';
import { imageUrls } from 'src/app/_shared/utils/images';
import { ALL_BATTERY_TYPES } from 'src/app/_shared/utils/battery-type';

@Component({
  selector: 'app-atomic-aging',
  templateUrl: './atomic-aging.component.html',
  styleUrls: ['./atomic-aging.component.scss']
})
export class AtomicAgingComponent implements OnInit, OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() fleetId: string[] = [];
  @Input() batteryType: string[] = [];
  @Input() companyData: any;
  @Input() isMetric: boolean | undefined;
  imageUrls = imageUrls;
  private readonly MAIN_FACTORS = [
    'Share of Fast Charging Sessions',
    'Charging at Low Temperatures',
    'State of Charge (SoC) > 80%',
    'High Battery Temperature'
  ];
  private readonly FACTOR_NAME_ALIASES: Record<string, string[]> = {
    'Share of Fast Charging Sessions': ['Share of Fast Charging Sessions', 'High Power Charging'],
    'Charging at Low Temperatures': ['Charging at Low Temperatures'],
    'State of Charge (SoC) > 80%': ['State of Charge (SoC) > 80%', 'highSoc'],
    'High Battery Temperature': ['High Battery Temperature', 'Temperature > 30\u00b0C']
  };
  backgroundZones: ProgressZone[] = [
    { from: 0, to: 60, color: 'var(--bosch-gray-80)' },
    { from: 60, to: 80, color: 'var(--bosch-blue-80)' },
    { from: 80, to: 100, color: 'var(--bosch-green-80)' }
  ];

  stressFactors: any[] = [];
  selectedVehicles: any[] = [];
  detailTitle: string | null = null;
  // API responses
  private fleetApiResponse: any = {};
  private evApiResponse: any = {};
  dataLoading: boolean = false;
  private readonly kmToMilesPipe = new KmToMilesPipe();

  constructor(
    private readonly fleetGeneralFigureService: FleetGeneralFigureService,
    private readonly vehicleGeneralFigureService: VehicleGeneralFigureService,
    private readonly vehicleInfoService: VehicleInfoService
    ,
    private readonly navigationService: NavigationService
  ) { }

  ngOnInit(): void {
    this.loadFleetData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['fleetId'] || changes['batteryType']) && !changes['fleetId']?.isFirstChange()) {
      this.loadFleetData();
    }
  }

  private loadFleetData(): void {
    if (!Array.isArray(this.fleetId) || this.fleetId.length === 0) return;
    this.dataLoading = true;
    this.fleetGeneralFigureService.getAtomicAgingFleet(this.fleetId, this.batteryType).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: any) => {
        this.fleetApiResponse = data || {};
        // Build canonical stressFactors list containing MAIN_FACTORS
        const values = Object.values(this.fleetApiResponse || {});
        const entry = values.length > 0 ? (values[0] as any) : null;
        const fleetFactors: any[] = entry?.stressFactors ?? [];
        this.stressFactors = this.MAIN_FACTORS
          .map(name => {
            const found = (fleetFactors || []).find(f => this.factorNamesMatch(f.stressFactorName, name));
            if (!found || found.score == null) {
              return null;
            }

            return {
              stressFactorName: name,
              score: found.score ?? null,
              deltaScore: found.deltaScore ?? null,
              loss: found.loss ?? null,
              rating: found.rating ?? null,
              ratingIntervals: found.ratingIntervals ?? [0, 60, 80, 100],
              totalNumberOfVehiclesConsidered: found.totalNumberOfVehiclesConsidered ?? null,
              warningIcon: found.warningIcon ?? false,
              detailsAvailable: false
            };
          })
          .filter((factor): factor is any => factor !== null);

        // attempt to fetch per-vehicle atomic aging in batches (if vehicle ids are available)
        this.fetchPerVehicleAtomicAging();
      },
      error: err => {
        console.error('Error fetching fleet atomic aging:', err);
      },
      complete: () => this.dataLoading = false
    });
  }

  private fetchPerVehicleAtomicAging(): void {
    // get all vehicles for fleet (pageSize 0 to return all) to extract battery identifiers
    this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (dataList: any) => {
        // The vehicles API has no batteryType query parameter, so the list is filtered client-side.
        // `all_battery_types` ("Entire fleet") means "no filter": every vehicle is kept.
        const batteryTypeFilter = this.batteryType.filter((type) => type !== ALL_BATTERY_TYPES);
        const allVehicles = dataList?.data || [];
        const filteredVehicles = batteryTypeFilter.length === 0
          ? allVehicles
          : allVehicles.filter((v: any) => batteryTypeFilter.includes(v.batteryType));
        const devices: string[] = filteredVehicles.map((v: any) => v.batteryIdentifier || v.deviceSerialNumber).filter(Boolean);
        if (!devices || devices.length === 0) return;

        const batchSize = 50;
        const requests: any[] = [];
        for (let i = 0; i < devices.length; i += batchSize) {
          const chunk = devices.slice(i, i + batchSize);
          requests.push(this.vehicleGeneralFigureService.getAtomicAging(chunk));
        }

        if (requests.length === 0) return;

        forkJoin(requests).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: (resArray: any[]) => {
                this.mergeVehicleResponses(resArray);
                this.updateDetailsAvailability();
          },
          error: err => {
            console.error('Error fetching atomic aging in batches:', err);
          }
        });
      },
      error: err => console.error('Error fetching vehicles for atomic aging:', err)
    });
  }

  private mergeVehicleResponses(resArray: any[]): void {
    this.evApiResponse = this.evApiResponse || {};
    resArray
      .filter(response => response && typeof response === 'object')
      .forEach(response => {
        Object.keys(response).forEach(key => { this.evApiResponse[key] = response[key]; });
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  nameMatches(a: string | undefined, b: string | undefined): boolean {
    if (!a || !b) return false;
    const normalize = (s: string) => s.replace(/[^a-z0-9]/gi, '').toLowerCase();
    return normalize(a).includes(normalize(b)) || normalize(b).includes(normalize(a));
  }

  private factorNamesMatch(apiName: string | undefined, uiName: string | undefined): boolean {
    if (!apiName || !uiName) return false;
    const aliases = this.FACTOR_NAME_ALIASES[uiName] ?? [uiName];
    return aliases.some(alias => this.nameMatches(apiName, alias));
  }

  getStressFactorsRowClass(): string {
    const count = this.stressFactors?.length ?? 0;
    const boundedCount = Math.min(Math.max(count, 1), 4);
    return `row-cols-${boundedCount}`;
  }

  private updateDetailsAvailability(): void {
    // For each main factor, mark detailsAvailable true if any vehicle in evApiResponse has a non-null score for it
    const vehicles = Object.values(this.evApiResponse || {}) as any[];
    for (const sf of this.stressFactors) {
      let avail = false;
      for (const v of vehicles) {
        const arr = (v?.stressFactors || []);
        if (!Array.isArray(arr)) continue;
        const match = arr.find((s: any) => this.factorNamesMatch(s.stressFactorName, sf.stressFactorName));
        if (match && match.score != null) { avail = true; break; }
      }
      sf.detailsAvailable = avail;
    }
  }

  toggleDetails(sf: any): void {
    if (this.detailTitle === sf.stressFactorName) {
      this.detailTitle = null;
      this.selectedVehicles = [];
      return;
    }
    const buildFromEvResponse = (source: any) => {
      const entries = Object.entries(source || {}) as [string, any][];
      const found: any[] = [];
      for (const [vehId, data] of entries) {
        const sfMatch = (data?.stressFactors || []).find((s: any) => this.factorNamesMatch(s.stressFactorName, sf.stressFactorName));
        if (sfMatch && sfMatch.score != null) {
          found.push({
            id: vehId,
            vehicleName: data?.name,
            score: sfMatch.score,
            deltaScore: sfMatch.deltaScore,
            loss: sfMatch.loss,
            rating: sfMatch.rating,
            ratingIntervals: sfMatch.ratingIntervals,
            warningIcon: sfMatch.warningIcon ?? false,
            urlImage: sfMatch.urlImage ?? data?.urlImage ?? null
          });
        }
      }
      found.sort((a, b) => (b.deltaScore ?? Number.NEGATIVE_INFINITY) - (a.deltaScore ?? Number.NEGATIVE_INFINITY));
      return found.slice(0, 5);
    };

    const buildFromFleetResponse = () => {
      // Try to find per-vehicle entries inside the fleet API response if present
      const entries = Object.entries(this.fleetApiResponse || {}) as [string, any][];
      const found: any[] = [];
      for (const [, data] of entries) {
        // if data is a mapping of vehicleId -> vehicleData
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          for (const [vehId, vehData] of Object.entries(data)) {
            const sfMatch = (vehData as any)?.stressFactors?.find((s: any) => this.factorNamesMatch(s.stressFactorName, sf.stressFactorName));
            if (sfMatch && sfMatch.score != null) {
              found.push({
                id: vehId,
                vehicleName: (vehData as any)?.name,
                score: sfMatch.score,
                deltaScore: (sfMatch as any)?.deltaScore,
                loss: sfMatch.loss,
                rating: sfMatch.rating,
                ratingIntervals: sfMatch.ratingIntervals,
                warningIcon: sfMatch.warningIcon ?? false,
                urlImage: (sfMatch as any)?.urlImage ?? (vehData as any)?.urlImage ?? null
              });
            }
          }
        }
      }
      found.sort((a, b) => (b.deltaScore ?? Number.NEGATIVE_INFINITY) - (a.deltaScore ?? Number.NEGATIVE_INFINITY));
      return found.slice(0, 5);
    };
    // Prefer per-vehicle API response (batched) if available
    const fromEv = buildFromEvResponse(this.evApiResponse);
    if (fromEv.length > 0) {
      this.selectedVehicles = fromEv;
      this.detailTitle = sf.stressFactorName;
      return;
    }

    // Fallback to fleet response
    const fromFleet = buildFromFleetResponse();
    if (fromFleet.length > 0) {
      this.selectedVehicles = fromFleet;
      this.detailTitle = sf.stressFactorName;
      return;
    }

    // No per-vehicle data available — clear selection and set title
    this.selectedVehicles = [];
    this.detailTitle = sf.stressFactorName;
  }

  getConvertedValuesByUnit(arr: number[]): number[] {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? arr : arr.map(val => this.kmToMilesPipe.transform(val));
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? arr.map(val => this.kmToMilesPipe.transform(val))
        : arr;
    }
  }

  getMileageUnit(): string {
    if (this.isMetric !== null && this.isMetric !== undefined) {
      return this.isMetric ? 'Mileage [km]' : 'Mileage [mi]';
    } else {
      return this.companyData?.companyAddress?.country === 'United States (US)'
        ? 'Mileage [mi]'
        : 'Mileage [km]';
    }
  }

  formatLoss(loss: number | null | undefined): string {
    if (loss === null || loss === undefined) return 'N/A';
    const converted = this.getConvertedValuesByUnit([loss])[0];
    const unit = this.getMileageUnit().includes('mi') ? 'mi' : 'km';
    return converted != null ? (converted.toFixed(2) + unit + ' (In 1 Year)') : 'N/A';
  }

  goToInsights(vehicleId: string) {
    this.navigationService.navigateTo('insights', vehicleId);
  }

  getColor(rating: string): string {
    if (!rating) return 'var(--bosch-blue-50)';
    switch (rating.toLowerCase()) {
      case 'optimal': return 'var(--bosch-green-50)';
      case 'high': return 'var(--bosch-green-50)';
      case 'medium': return 'var(--bosch-blue-50)';
      case 'mid': return 'var(--bosch-blue-50)';
      case 'low': return 'var(--bosch-purple-50)';
      default: return 'var(--bosch-blue-50)';
    }
  }

  getZones(ratingIntervals: number[] | undefined): ProgressZone[] {
    if (!Array.isArray(ratingIntervals) || ratingIntervals.length < 2) {
      return this.backgroundZones;
    }
    
    const ranges: ProgressZone[] = [];
    const colors = ['var(--bosch-gray-80)', 'var(--bosch-blue-80)', 'var(--bosch-green-80)'];
    for (let i = 0; i < ratingIntervals.length - 1; i++) {
      ranges.push({
        from: ratingIntervals[i],
        to: ratingIntervals[i + 1],
        color: colors[Math.min(i, colors.length - 1)]
      });
    }
    return ranges;
  }

  getBgColor(name: string | null | undefined): string {
    if (!name) return 'var(--bosch-yellow-90)';
    switch (name) {
      case 'Share of Fast Charging Sessions':
        return 'var(--bosch-red-90)';
      case 'Charging at Low Temperatures':
        return 'var(--bosch-blue-90)';
      case 'State of Charge (SoC) > 80%':
        return 'var(--bosch-purple-90)';
      case 'High Battery Temperature':
        return 'var(--bosch-yellow-90)';
      default:
        return 'var(--bosch-yellow-90)';
    }
  }

}
